import {
  prisma,
  type Prisma,
  DocumentEntityType,
  ParticipantGroupType,
  VideoType,
} from "client";
import { getParticipantEntityIdForPortal } from "../product";

// CREATE

export const createVideo = async (data: Prisma.VideoCreateInput) => {
  try {
    return await prisma.video.create({ data });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// GET

export const getVideo = async (id: string) => {
  return await prisma.video.findUnique({
    where: { id },
    include: { translations: true },
  });
};

export const getVideosByEntityId = async (
  entityId: string,
  lang: string,
  search?: string,
  userId?: string,
  eventId?: string,
  entityType?: DocumentEntityType
) => {
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const searchParam = search ? escapeRegex(search) : "";
  const filter = searchParam
    ? {
        OR: [
          {
            translations: {
              some: {
                OR: [
                  {
                    name: {
                      contains: searchParam,
                      mode: "insensitive" as const,
                    },
                  }, // Add 'as const'
                ],
              },
            },
          },
        ],
      }
    : {};
  const videos = await prisma.video.findMany({
    where: {
      entityId,
      ...filter,
      ...(entityType ? { entityType } : {}),
    },
    include: {
      // Without userId, Prisma drops the where clause and returns every user's bookmarks.
      // Keep an empty array shape so callers can still read `.bookmarks.length`.
      bookmarks: userId
        ? {
            where: {
              userId,
              ...(eventId ? { eventId } : {}),
            },
          }
        : false,
      translations: lang
        ? {
            where: { language: { code: lang } },
          }
        : true,
    },
  });
  return videos.map((video) => ({
    ...video,
    bookmarks: userId ? (video.bookmarks ?? []) : [],
    name: video.translations?.[0]?.name,
    url: video.translations?.[0]?.url,
  }));
};

// UPDATE

export const updateVideo = async (
  id: string,
  data: Prisma.VideoUpdateInput
) => {
  try {
    return await prisma.video.update({ where: { id }, data });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// DELETE

export const deleteVideo = async (id: string) => {
  return await prisma.video.delete({ where: { id } });
};

export type PortalVideoRow = {
  id: string;
  name: string;
  url: string;
  type: VideoType;
  translationId?: string;
  languageId?: string;
  translations: {
    id: string;
    name: string;
    url: string;
    languageId: string;
  }[];
};

export type VideoSearchPage = {
  items: PortalVideoRow[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
};

export const VIDEO_PAGE_SIZE = 12;
export const LEGACY_VIDEO_LIST_CAP = 500;

const portalVideoSelect = {
  id: true,
  type: true,
  translations: {
    select: {
      id: true,
      name: true,
      url: true,
      languageId: true,
      language: { select: { code: true } },
    },
  },
} as const;

const mapPortalVideos = (
  videos: {
    id: string;
    type: VideoType;
    translations: {
      id: string;
      name: string;
      url: string;
      languageId: string;
      language: { code: string };
    }[];
  }[],
  locale: string
): PortalVideoRow[] =>
  videos.flatMap((video) => {
    if (!video.translations.length) return [];
    const display =
      video.translations.find((t) => t.language.code === locale) ??
      video.translations[0];
    return [
      {
        id: video.id,
        type: video.type,
        name: display.name,
        url: display.url,
        translationId: display.id,
        languageId: display.languageId,
        translations: video.translations.map(
          ({ id, name, url, languageId }) => ({
            id,
            name,
            url,
            languageId,
          })
        ),
      },
    ];
  });

const buildVideoSearchWhere = (
  entityId: string,
  search?: string,
  entityType?: DocumentEntityType
) => {
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const searchParam = search?.trim() ? escapeRegex(search.trim()) : "";
  return {
    entityId,
    ...(entityType ? { entityType } : {}),
    translations: searchParam
      ? {
          some: {
            name: {
              contains: searchParam,
              mode: "insensitive" as const,
            },
          },
        }
      : { some: {} },
  };
};

export const searchVideosByEntityId = async ({
  entityId,
  locale,
  search = "",
  page = 1,
  pageSize = VIDEO_PAGE_SIZE,
  entityType,
  all = false,
}: {
  entityId: string;
  locale: string;
  search?: string;
  page?: number;
  pageSize?: number;
  entityType?: DocumentEntityType;
  all?: boolean;
}): Promise<VideoSearchPage> => {
  const where = buildVideoSearchWhere(entityId, search, entityType);
  const safePage = all ? 1 : Math.max(1, page);
  const safePageSize = all
    ? LEGACY_VIDEO_LIST_CAP
    : Math.min(Math.max(1, pageSize), 50);

  const [total, videos] = await Promise.all([
    prisma.video.count({ where }),
    prisma.video.findMany({
      where,
      select: portalVideoSelect,
      orderBy: { createdAt: "desc" },
      skip: all ? 0 : (safePage - 1) * safePageSize,
      take: safePageSize,
    }),
  ]);

  return {
    items: mapPortalVideos(videos, locale),
    total,
    page: safePage,
    pageSize: safePageSize,
    hasMore: safePage * safePageSize < total,
  };
};

export const getParticipantVideosForPortal = async (
  slug: string,
  userId: string,
  eventId: string,
  type: ParticipantGroupType,
  locale: string,
  entityType: DocumentEntityType,
  search = "",
  page = 1,
  pageSize = VIDEO_PAGE_SIZE
) => {
  const participant = await getParticipantEntityIdForPortal(
    slug,
    userId,
    eventId,
    type
  );
  if (!participant) return null;

  const videosPage = await searchVideosByEntityId({
    entityId: participant.id,
    locale,
    search,
    page,
    pageSize,
    entityType,
  });

  return { id: participant.id, ...videosPage };
};

export const assertVideoBelongsToEntity = async (
  videoId: string,
  entityId: string
) => {
  return prisma.video.findFirst({
    where: { id: videoId, entityId },
    select: { id: true, entityType: true },
  });
};
